import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IBeforeWinAnimationConfiguration } from '@GFTypes/GameStoreTypes';
import EventsCenter from '@Managers/Events/EventsCenter';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import { DeferredPromise } from '@Tools/DeferredPromise';
import { EBeforeWinAnimationEvents } from './BeforeWinAnimationEvents';

export class BeforeWinAnimation extends GameComponent {
    static readonly SPINE_KEY: string = 'beforeWinAnimation';
    static readonly SKIN_NAME: string = 'default';
    private entryCondition: EntryCondition<EGameStates>;
    private leaveCondition: LeaveCondition<EGameStates>;
    private skipPromise: DeferredPromise;
    private animationLoopPromise: DeferredPromise;
    private spine: SpineWrapper;
    private config: IBeforeWinAnimationConfiguration;
    constructor(public readonly scene: GameScene) {
        super(scene);
        Logger.log(LoggerLevel.Constructor, { class: BeforeWinAnimation.name, source: LoggerSource.Game, message: 'PreWinAnimation constructor' });
    }

    public onCreate(): void {
        this.config = this.gameStore.get(EGameStoreKeys.BeforeWinAnimationConfiguration);
        this.skipPromise = new DeferredPromise();
        this.animationLoopPromise = new DeferredPromise();
        this.leaveCondition = new LeaveCondition<EGameStates>('waitForMultiplierFeature');
        this.entryCondition = new EntryCondition<EGameStates>('checkMultiplierFeature', () => {
            const animationExists = this.scene.baseWinPresentationSpine.eventManager.hasGameEventAnimation(
                EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_IN,
                true
            );
            return animationExists && this.gameStore.get(EGameStoreKeys.SpinOutcome).win > 0;
        });

        const subStates = {
            PRE_BEFORE_WIN_ANIMATION_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: []
            },
            BEFORE_WIN_ANIMATION_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: [this.leaveCondition]
            },
            POST_BEFORE_WIN_ANIMATION_STATE: {
                entryConditions: [this.entryCondition],
                leaveConditions: []
            }
        };

        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, subStates);

        this.setAnimation();

        EventsCenter.on(EGameStates.BEFORE_WIN_ANIMATION_STATE, this.featureSequence, this);
    }

    private setAnimation(): void {
        this.spine = this.scene.baseWinPresentationSpine;
    }

    private async featureSequence(): Promise<void> {
        this.skipPromise.reset();
        this.animationLoopPromise.reset();
        this.setAnimation();
        this.spine.spine.visible = true;

        if (!this.spine.eventManager.getAnimationNameForGameEvent(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_IN)) return this.leaveCondition.resolve();

        EventsCenter.emit(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_IN);

        await Promise.race([this.spine.waitForAnimationEvent(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_IN, 'complete').promise, this.skipPromise.promise]);

        EventsCenter.emit(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_LOOP);

        this.scene.time.delayedCall(this.config.loopDuration, this.animationLoopPromise.resolve);
        await Promise.race([this.animationLoopPromise.promise, this.skipPromise.promise]);

        EventsCenter.emit(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_OUT);

        await Promise.race([
            this.spine.waitForAnimationEvent(EBeforeWinAnimationEvents.BEFORE_WIN_ANIMATION_OUT, 'complete').promise,
            this.skipPromise.promise
        ]);

        this.leaveCondition.resolve();
    }
}
